import React, { Fragment, FunctionComponent, useState } from 'react';

import TableRow, { Column } from './TableRow/TableRow';
import type { Row, Columns } from './TableRow/TableRow';

import styles from './Table.module.scss';
import Button from '../../../Input/Button/Button';
import Icon from '../../Icon/Icon';
import Box from '../../../Layout/Box/Box';
import SubTitle from '../../../Text/SubTitle/SubTitle';

export type TableProps = {
  id?: string;
  name: string;
  variant?:
    | 'record'
    | 'pair'
    | 'chart'
    | 'railz'
    | 'progress'
    | 'overview'
    | 'form'
    | 'plain'
    | 'basic'
    | 'detail'
    | 'splitPayment'
    | 'minimal';
  hasNoHead?: boolean;
  hasNoMiWidth?: boolean;
  width?: string;
  columns: Columns;
  sortable?: boolean;
  sortFunction?: (setSortDirection: 'asc' | 'desc' | null, sortColumn: string | null) => void;
  rows: Row[];
  expandedRows?: (React.JSX.Element | undefined)[];
  isExpandable?: boolean;
  title?: string;
};

type FC = FunctionComponent<TableProps>;

const Table: FC = ({
  id,
  name,
  variant = 'record',
  hasNoHead,
  hasNoMiWidth,
  width = '50%',
  columns,
  rows,
  sortFunction,
  sortable = false,
  expandedRows = [],
  isExpandable = false,
  title,
}: TableProps) => {
  const columnKeys = Object.keys(columns).filter(
    (key) => columns[key] && (columns[key]?.visiblity === undefined || columns[key]?.visiblity === true),
  );
  const columnValues = Object.values(columns).filter((column): column is Column => isColumn(column));
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  function handleHeaderClick(columnKey: string) {
    if (sortable) {
      if (sortColumn === columnKey) {
        // If the same column is clicked again, toggle the sort direction
        setSortDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        // If a new column is clicked, set it as the sort column and default to ascending sort
        setSortColumn(columnKey);
        setSortDirection('asc');
      }
    }
  }
  const sortedRows = React.useMemo(() => {
    if (sortFunction) {
      sortFunction(sortDirection, sortColumn);
      return rows;
    }
    if (!sortColumn || !sortDirection) {
      // If no sorting is applied, return the original rows array
      return rows;
    }

    // Sort the rows array based on the sortColumn and sortDirection
    return rows.slice().sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      // Adjust the comparison based on the data type of the column
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return sortDirection === 'asc' ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
    });
  }, [rows, sortColumn, sortDirection]);
  return (
    <>
      {title ? (
        <div className={styles.title}>
          <SubTitle subTitle={title} variant="small" />
        </div>
      ) : (
        <></>
      )}
      <table
        id={id}
        className={[styles.container, styles[getTableLayoutClass()], styles[getNoMinWidthClass()]].join(' ')}
        data-testid={`table-${name}`}
      >
        <thead className={[styles.header, styles[getTableLayoutClass()], styles[getHeadClass()]].join(' ')}>
          <tr className={styles.headerText}>
            {columnValues.map(({ header, align, isSwap, onSwap, isSortable = true }, idx) => (
              <th
                key={`header-${idx}`}
                className={`${styles[getPositionClass(idx, align)]} ${
                  sortColumn === columnKeys[idx] ? styles[sortDirection || ''] : ''
                }`}
                onClick={() => handleHeaderClick(columnKeys[idx])}
              >
                <Box flx="row" ai="center">
                  <div style={{ width: !sortable ? '100%' : 'auto' }}>
                    {header}
                    {isSwap && (
                      <Button
                        label=" "
                        id="swap"
                        icon="swap"
                        noPadding
                        noShadow
                        background="transparent"
                        color="secondary"
                        iconWidth="23.5px"
                        iconHeight="13.5px"
                        iconLocation="center"
                        iconSize="sm"
                        onClick={() => {
                          if (typeof onSwap !== 'undefined') {
                            onSwap();
                          }
                        }}
                      />
                    )}
                  </div>
                  {sortable && isSortable && (
                    <Box flx="col" gapSize="2px">
                      {(sortDirection === 'asc' || sortColumn !== columnKeys[idx]) && (
                        <Icon icon="angleTop" height="6.5px" width="30px" color="dark-grey" />
                      )}
                      {(sortDirection !== 'asc' || sortColumn !== columnKeys[idx]) && (
                        <Icon icon="angleDown" height="6.5px" width="30px" color="dark-grey" />
                      )}
                    </Box>
                  )}
                </Box>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedRows.map((row, idx) => (
            <Fragment key={`${name}-row-${idx}`}>
              <TableRow
                tableId={name}
                row={row}
                rowIdx={idx}
                variant={variant}
                width={width}
                columns={columns}
                isExpandable={isExpandable}
              />

              {expandedRows[idx] && (
                <tr className={styles.expandedRowContainer} id={`${name}-cell-expanded-${idx}`}>
                  <td className={styles.expandedRow} colSpan={columnKeys.length}>
                    {expandedRows[idx]}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  );

  function getPositionClass(idx: number, align?: 'left' | 'right' | 'center'): string {
    if (align) return align;
    if (variant === 'pair') return 'left';
    return ['left'][idx] || (idx === columnKeys.length - 1 ? 'center' : 'center');
  }

  function getTableLayoutClass() {
    return {
      pair: 'fixed',
      record: '',
      chart: 'chart',
      progress: 'progress',
      overview: 'overview',
      form: 'form',
      plain: 'plain',
      railz: 'railz',
      basic: 'basic',
      detail: 'detail',
      splitPayment: 'splitPayment',
      minimal: 'minimal',
    }[variant];
  }

  function getHeadClass() {
    return hasNoHead ? 'no-head' : '';
  }

  function isColumn(column: Column | null): column is Column {
    return column !== null && (column.visiblity === undefined || column.visiblity === true);
  }

  function getNoMinWidthClass() {
    return hasNoMiWidth ? 'noMinWidth' : '';
  }
};

export default Table;
